// Iter 95 — Centralised DOMPurify wrappers for Gmail HTML rendering.
//
// Two sanitiser profiles:
//   * `sanitizeEmailHtml` — for full email bodies (Reply/Forward quotes,
//     timeline previews, threaded message bodies). Mirrors the original
//     HTML_SANITIZE_CONFIG from Triage.jsx so behaviour stays identical
//     and the threaded view + the composer quote pane render the same.
//   * `sanitizeHighlightInline` — for tiny server-rendered snippets that
//     ONLY carry a `<mark>` tag for search-term highlighting (subject /
//     snippet rows in GmailSearchPalette). Strips everything else.
//
// All XSS-prone `dangerouslySetInnerHTML` callers MUST funnel through
// one of these helpers — never call DOMPurify.sanitize ad-hoc.
import DOMPurify from "dompurify";

const EMAIL_HTML_CONFIG = {
  ALLOWED_TAGS: [
    "a","b","i","em","strong","u","p","br","ul","ol","li","blockquote",
    "span","div","table","thead","tbody","tr","td","th",
    "h1","h2","h3","h4","h5","h6","img","hr","pre","code","mark",
  ],
  ALLOWED_ATTR: ["href","title","alt","src","target","style","width","height","colspan","rowspan"],
  FORBID_TAGS: ["script","style","iframe","object","embed","form","input","textarea","head","title","meta","link","html","body"],
  FORBID_CONTENTS: ["style","script","head","title","meta","link"],
  ADD_ATTR: ["target"],
  WHOLE_DOCUMENT: false,
};

const HIGHLIGHT_INLINE_CONFIG = {
  ALLOWED_TAGS: ["mark","b","strong","em","i","span"],
  ALLOWED_ATTR: [],
  FORBID_TAGS: ["script","style","iframe","object","embed","form","input","textarea","img","a"],
  FORBID_CONTENTS: ["style","script"],
  WHOLE_DOCUMENT: false,
};

export function sanitizeEmailHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, EMAIL_HTML_CONFIG);
}

export function sanitizeHighlightInline(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, HIGHLIGHT_INLINE_CONFIG);
}

// Re-export the email config so legacy callers that pass it explicitly
// (e.g. existing Triage.jsx body renders) keep working without changes.
export const HTML_SANITIZE_CONFIG = EMAIL_HTML_CONFIG;

/**
 * Iter 110q — Decode HTML entities in a plain-text string for safe
 * display. Used by inbox-row snippets and toast bodies which arrive as
 * "plain text but entity-encoded" (Gmail's snippet field, our DOMPurify
 * stripped body fallbacks). Without this, users see literal `It&#39;s`
 * / `&amp;` / `&quot;` in the row instead of `It's` / `&` / `"`.
 *
 * Implementation: roundtrip through a detached `<textarea>` element so
 * the browser's HTML parser does the heavy lifting (handles named
 * entities, numeric `&#39;`, hex `&#x27;`). We read `.value` (not
 * `.innerHTML`), so no script context is created — this is safe even
 * for hostile input. Returns the input unchanged for non-strings /
 * empty.
 */
export function decodeHtmlEntities(s) {
  if (!s) return "";
  const str = String(s);
  // Fast path — no entity present, nothing to decode.
  if (str.indexOf("&") === -1) return str;
  if (typeof document === "undefined") {
    // SSR / Node fallback — handle the common five plus numeric entities.
    return str
      .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
      .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&nbsp;/g, " ");
  }
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
}
